import { useEffect } from 'react'
import { wipeAll } from '@/core/storage/schema-version'
import { useSettingsStore } from '@/core/storage/zustand-store'

export interface TTLGuardOptions {
  /** Override for tests; defaults to wipeAll from schema-version. */
  wipe?: () => void
  /** Override for tests; defaults to window.location.reload(). */
  reload?: () => void
  /** Override for tests; defaults to new Date(). */
  now?: () => Date
}

export interface TTLGuardOutcome {
  /** True iff the guard fired and wiped storage. */
  wiped: boolean
}

/**
 * Runs at app boot in paranoid mode. If `lastAccessedAt` is older than
 * `paranoidMode.ttlDays`, wipes the namespaced localStorage and triggers
 * a reload so the in-memory state is rebuilt from defaults. When the
 * guard does not fire it just touches `lastAccessedAt` so future visits
 * are measured against the most recent open.
 *
 * Pure helper that can be invoked from a useEffect or driven from a test.
 */
export function evaluateTTLGuard(options: TTLGuardOptions = {}): TTLGuardOutcome {
  const wipe = options.wipe ?? wipeAll
  const reload = options.reload ?? ((): void => window.location.reload())
  const now = options.now ?? ((): Date => new Date())

  const settings = useSettingsStore.getState().settings
  const lastMs = new Date(settings.lastAccessedAt).getTime()

  if (
    settings.paranoidMode.enabled &&
    Number.isFinite(lastMs) &&
    lastMs > 0
  ) {
    const elapsedMs = now().getTime() - lastMs
    const ttlMs = settings.paranoidMode.ttlDays * 24 * 60 * 60 * 1000
    if (elapsedMs > ttlMs) {
      wipe()
      reload()
      return { wiped: true }
    }
  }

  useSettingsStore.getState().patch({ lastAccessedAt: now().toISOString() })
  return { wiped: false }
}

export function useTTLGuard(options: TTLGuardOptions = {}): void {
  useEffect(() => {
    evaluateTTLGuard(options)
    // Intentional: run exactly once at mount. Re-running on options
    // identity churn would defeat the "single boot check" semantics.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
